import robustFetch from "@/utils/robustFetch";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface GuestOnlyGuardProps {
  children: React.ReactNode;
}

export default function GuestOnlyGuard({ children }: GuestOnlyGuardProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      // Verify token is still valid by checking with server
      const response = await robustFetch("/api/verification-status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const status = await response.json();
        setIsAuthenticated(status.authenticated);

        if (status.authenticated) {
          // User is authenticated, redirect them to their dashboard
          navigate("/my-files", { replace: true });
          return;
        }
      } else {
        // Invalid token, remove it
        if (response.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user_data");
          // SECURITY FIX: Clear session on auth failure
          localStorage.removeItem("session_id");
          const newGuestSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem("session_id", newGuestSession);
        }
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Error checking authentication:", error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"></div>
          <div
            className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"
            style={{ animationDelay: "0.2s" }}
          ></div>
          <div
            className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"
            style={{ animationDelay: "0.4s" }}
          ></div>
        </div>
      </div>
    );
  }

  // If user is authenticated, don't render anything (redirect already happened)
  if (isAuthenticated) {
    return null;
  }

  // User is not authenticated, show the guest-only content (login/signup)
  return <>{children}</>;
}
